import { PushSubscriptionData, isPushNotificationSupported, subscribeToPush, serializePushSubscription, registerServiceWorker } from '../utils/pushNotifications';
import { getSafeApiUrl, isDevelopment, getVapidPublicKey } from '../config/environment';
const USE_FCM = false;

const API_BASE_URL = getSafeApiUrl('NOTIFICATION');

interface NotificationSettings {
  // 5개 알림 타입별 설정
  dailyReport: boolean; // 데일리 리포트 (매일 7:00)
  inboundReminder: boolean; // 입고 완료 처리 (매일 9:00)
  realtimeLowStock: boolean; // 실시간 재고부족 (이벤트 기반)
  newMenu: boolean; // 등록되지 않은 메뉴 알림 (이벤트 기반)
  orderDeadline: boolean; // 발주마감 알림 (거래처별 동적)
  
  // 기존 필드 (하위 호환성)
  lowStock?: boolean; // realtimeLowStock과 동일 (하위 호환성)
  orderAvailable?: boolean;
  inboundComplete?: boolean;
  system?: boolean;
}

interface SubscribeRequest {
  deviceId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  settings: NotificationSettings;
  fcmToken?: string; // FCM registration token 추가
}

interface UpdateSettingsRequest {
  settings: NotificationSettings;
}

// 푸시 엔드포인트 만료 정책 (JWT 토큰과 동일: 30일 유효, 15일 전 갱신)
const ENDPOINT_VALID_DAYS = 30;
const ENDPOINT_REFRESH_BEFORE_DAYS = 15;
const ENDPOINT_REFRESH_COOLDOWN_MS = 12 * 60 * 60 * 1000; // 12시간 내 중복 갱신 방지

class PushNotificationService {
  private subscriptionState: boolean = false;
  private listeners: Array<(isSubscribed: boolean) => void> = [];
  private isAutoRefreshing: boolean = false; // 자동 토큰 갱신 중복 방지

  // 순수 모바일 체크 (iPad 제외)
  private isPureMobile(): boolean {
    return /Android|iPhone|iPod/i.test(navigator.userAgent);
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    const storeId = localStorage.getItem('storeId');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(storeId && { 'X-Store-Id': storeId })
    };
  }

  // 구독 상태 변경 리스너 등록
  public addSubscriptionListener(callback: (isSubscribed: boolean) => void): void {
    this.listeners.push(callback);
    // 현재 상태 즉시 전달
    callback(this.subscriptionState);
  }

  // 구독 상태 변경 리스너 제거
  public removeSubscriptionListener(callback: (isSubscribed: boolean) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // 시크릿 모드 감지 (모바일 환경 최적화)
  private isIncognitoMode(): boolean {
    try {
      // localStorage 접근 시도
      const testKey = '__incognito_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return false;
    } catch (e) {
      return true;
    }
  }

  // 구독 상태 업데이트 및 모든 리스너에 알림
  private updateSubscriptionState(isSubscribed: boolean): void {
    this.subscriptionState = isSubscribed;
    this.listeners.forEach(listener => listener(isSubscribed));
    console.log('📢 [전역 상태] 구독 상태 업데이트:', isSubscribed);
  }

  // Android 백그라운드 알림을 위한 설정
  private setupAndroidBackgroundNotifications(): void {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isChrome = /Chrome/i.test(navigator.userAgent);
    
    if (isAndroid && isChrome) {
      console.log('🤖 [Android 백그라운드] Android Chrome 감지 - 백그라운드 알림 설정');
      
      // Service Worker 등록 상태 확인
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          console.log('🤖 [Android 백그라운드] Service Worker 준비됨:', registration);
          
          // 백그라운드 동기화 등록
          if ('sync' in window.ServiceWorkerRegistration.prototype) {
            console.log('🤖 [Android 백그라운드] Background Sync 지원됨');
          }
          
          // 알림 권한 확인
          if (Notification.permission === 'granted') {
            console.log('🤖 [Android 백그라운드] 알림 권한 허용됨');
          } else {
            console.warn('🤖 [Android 백그라운드] 알림 권한 상태:', Notification.permission);
          }
        });
      }
    }
  }

  // 구독 상태 확인 및 복원 (기기별 구독 상태 확인)
  public async checkAndRestoreSubscription(): Promise<{ isSubscribed: boolean; needsTokenRefresh: boolean }> {
    // 순수 모바일이 아니면 즉시 종료
    if (!this.isPureMobile()) {
      console.log('🚫 [구독 상태 확인] 순수 모바일이 아님 - 백엔드 호출 차단');
      this.updateSubscriptionState(false);
      return { isSubscribed: false, needsTokenRefresh: false };
    }

    try {
      const userId = this.getUserId();
      const deviceId = this.getDeviceId();
      const storeId = this.getStoreId();

      console.log('🔍 [구독 상태 확인] UserId: {userId}, DeviceId: {deviceId}, StoreId: {storeId}');
      
      // 백엔드에서 이 기기의 구독 상태만 확인 (단순화)
      const apiUrl = `${API_BASE_URL}/push/subscriptions/${userId}/${deviceId}`;
      console.log('🔍 [구독 상태 확인] API 호출:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('🔍 [구독 상태 확인] 서버 응답:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ [구독 상태 확인] 서버 응답:', data);
        
        const isSubscribed = !!data.isSubscribed;
        const needsTokenRefresh = !!data.needsTokenRefresh;
        
        // 백엔드가 진실의 원천 - 백엔드 상태를 그대로 반영
        this.updateSubscriptionState(isSubscribed);
        
        if (needsTokenRefresh) {
          console.warn('🔄 [구독 상태 확인] 서버에서 토큰 갱신 필요 플래그 감지');
        }
        
        console.log('✅ [구독 상태 확인] 최종 결과:', data.isSubscribed);
        // 구독이 끊긴 경우 자동 복구 시도 (사용자가 비활성화하지 않았고 권한이 있을 때만)
        if (!isSubscribed && Notification.permission === 'granted') {
          const deviceStatus = this.getDeviceSubscriptionStatus();
          const userDisabled = localStorage.getItem('user_disabled_subscription') === 'true';
          if (deviceStatus && !userDisabled) {
            console.log('🔄 [구독 상태 확인] 구독 만료 감지 - 자동 재구독 시도');
            await this.requestAutoResubscribe('app-start-unsubscribed');
          } else {
            console.log('⏭️ [구독 상태 확인] 자동 재구독 조건 불충족 - 건너뜀');
          }
        }

        return { isSubscribed, needsTokenRefresh };
      } else {
        console.log('❌ [구독 상태 확인] 서버 응답 실패:', response.status);
        this.updateSubscriptionState(false);
        return { isSubscribed: false, needsTokenRefresh: false };
      }
    } catch (error) {
      console.error('❌ [구독 상태 확인] 구독 상태 확인 실패:', error);
      this.updateSubscriptionState(false);
      return { isSubscribed: false, needsTokenRefresh: false };
    }
  }

  async evaluateTokenRefreshOnAppStart(): Promise<void> {
    if (!USE_FCM) {
      return;
    }
    if (!this.isPureMobile()) {
      return;
    }

    const todayKey = new Date().toISOString().split('T')[0];
    const lastCheckKey = 'token_refresh_last_daily_check';
    const lastCheck = localStorage.getItem(lastCheckKey);
    if (lastCheck === todayKey) {
      console.log('⏭️ [토큰 검사] 금일 이미 검사 완료 - 건너뜀');
      return;
    }
    localStorage.setItem(lastCheckKey, todayKey);

    try {
      const status = await this.checkAndRestoreSubscription();
      if (!status.isSubscribed) {
        console.log('ℹ️ [토큰 검사] 구독 활성화 상태가 아니므로 검사 종료');
        return;
      }

      if (status.needsTokenRefresh) {
        console.log('⚠️ [토큰 검사] 백엔드에서 갱신 필요 플래그 확인 - 자동 재구독 시도');
        await this.requestAutoResubscribe('app-daily-check-backend');
        return;
      }

      await this.requestAutoResubscribe('app-daily-check-webpush');
    } catch (error) {
      console.error('❌ [토큰 검사] 자동 갱신 검사 중 오류:', error);
    }
  }

  // 플랫폼 및 브라우저 감지
  private getPlatformInfo() {
    const userAgent = navigator.userAgent;
    
    // 플랫폼 감지
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    const isWindows = /Windows/.test(userAgent);
    const isMac = /Macintosh|MacIntel/.test(userAgent);
    
    // 브라우저 감지
    const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);
    const isEdge = /Edge/.test(userAgent);
    
    return {
      platform: isIOS ? 'iOS' : isAndroid ? 'Android' : isWindows ? 'Windows' : isMac ? 'Mac' : 'Unknown',
      browser: isChrome ? 'Chrome' : isSafari ? 'Safari' : isFirefox ? 'Firefox' : isEdge ? 'Edge' : 'Unknown',
      isIOS,
      isAndroid,
      isChrome,
      isSafari
    };
  }

  // iOS 크롬 브라우저 감지
  private isIOSChrome(): boolean {
    const { isIOS, isChrome } = this.getPlatformInfo();
    return isIOS && isChrome;
  }

  // iOS Safari 브라우저 감지
  private isIOSSafari(): boolean {
    const { isIOS, isSafari } = this.getPlatformInfo();
    return isIOS && isSafari;
  }

  // iOS Safari 전용 디버깅 정보 수집
  private collectIOSSafariDebugInfo(): object {
    const userAgent = navigator.userAgent;
    const iosVersion = userAgent.match(/OS (\d+)_/)?.[1] || 'Unknown';
    const safariVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
    
    return {
      platform: 'iOS Safari',
      iosVersion,
      safariVersion,
      userAgent,
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasPushManager: 'PushManager' in window,
      hasNotification: 'Notification' in window,
      notificationPermission: Notification.permission,
      timestamp: new Date().toISOString(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      localStorageAvailable: (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return true;
        } catch {
          return false;
        }
      })()
    };
  }

  // Android 크롬 브라우저 감지
  private isAndroidChrome(): boolean {
    const { isAndroid, isChrome } = this.getPlatformInfo();
    return isAndroid && isChrome;
  }

  // 플랫폼별 푸시알림 지원 확인
  private isPushNotificationSupported(): boolean {
    const platformInfo = this.getPlatformInfo();
    const { platform, browser, isIOS, isAndroid, isChrome, isSafari } = platformInfo;
    
    console.log(`🔍 [플랫폼 감지] ${platform} - ${browser}`);
    
    // iOS 크롬 브라우저는 푸시알림 미지원
    if (isIOS && isChrome) {
      console.log('❌ [플랫폼] iOS 크롬 브라우저는 백그라운드 푸시알림을 지원하지 않습니다');
      return false;
    }
    
    // iOS Safari는 PWA 설치(standalone) 상태에서만 Web Push 지원
    if (isIOS && isSafari) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
      if (!isStandalone) {
        console.log('❌ [플랫폼] iOS Safari는 홈 화면에 추가된 PWA에서만 푸시알림을 지원합니다');
        return false;
      }
      console.log('✅ [플랫폼] iOS Safari PWA에서 Web Push 지원');
      return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }
    
    // Android 크롬은 푸시알림 완벽 지원
    if (isAndroid && isChrome) {
      console.log('✅ [플랫폼] Android 크롬은 백그라운드 푸시알림을 완벽 지원합니다');
      return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }
    
    // 기타 플랫폼/브라우저는 기본 Web Push API 지원 확인
    console.log(`⚠️ [플랫폼] ${platform} - ${browser}는 기본 Web Push API 지원 확인 중...`);
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // 앱 실행 시 1회: 푸시 엔드포인트 만료 확인 및 15일 전이면 자동 갱신 시도
  async checkAndRefreshEndpointOnStart(): Promise<void> {
    try {
      const endpointDataStr = localStorage.getItem('lastPushEndpoint');
      if (!endpointDataStr || !endpointDataStr.startsWith('{')) return;
      const endpointData = JSON.parse(endpointDataStr);
      if (!endpointData.expires) return;
      const now = Date.now();
      const timeUntilExpiry = endpointData.expires - now;
      if (timeUntilExpiry <= 0) return; // 만료됨 → getValidEndpointFromLocalStorage에서 처리
      if (timeUntilExpiry > (ENDPOINT_REFRESH_BEFORE_DAYS * 24 * 60 * 60 * 1000)) return; // 15일 초과 시 갱신 불필요
      const lastAttempt = localStorage.getItem('lastEndpointRefreshAttempt');
      if (lastAttempt && now - parseInt(lastAttempt, 10) < ENDPOINT_REFRESH_COOLDOWN_MS) return;
      localStorage.setItem('lastEndpointRefreshAttempt', now.toString());
      await this.requestAutoResubscribe('endpoint-app-start-15day');
    } catch {
      // 무시
    }
  }

  // 앱 초기화 시 구독 상태 복원 (플랫폼별 최적화)
  async initializeSubscription(): Promise<void> {
    // 앱 실행 시 1회: 엔드포인트 만료 확인 및 15일 전이면 갱신 시도
    this.checkAndRefreshEndpointOnStart().catch(() => {});

    // 순수 모바일이 아니면 즉시 종료
    if (!this.isPureMobile()) {
      console.log('🚫 [초기화] 순수 모바일이 아님 - 구독 초기화 차단');
      return;
    }

    try {
      console.log('🔄 [초기화] 구독 상태 복원 시작...');
      
      // 플랫폼별 푸시알림 지원 확인
      if (!this.isPushNotificationSupported()) {
        console.log('❌ [초기화] 현재 플랫폼에서 푸시알림을 지원하지 않습니다');
        this.updateSubscriptionState(false);
        return;
      }
      
      // 시크릿 모드 확인
      if (this.isIncognitoMode()) {
        console.log('🔒 [초기화] 시크릿 모드 감지 - 알림 기능 제한');
        return;
      }
      
      
      // Service Worker가 완전히 준비될 때까지 대기
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 브라우저 알림 권한 확인
      if (Notification.permission !== 'granted') {
        console.log('❌ [초기화] 알림 권한이 허용되지 않음');
        this.updateSubscriptionState(false);
        return;
      }

      // localStorage에서 구독 상태 확인
      const deviceId = this.getDeviceId();
      const storeId = this.getStoreId();
      const subscriptionKey = `subscription_${storeId}_${deviceId}`;
      const localSubscriptionStatus = localStorage.getItem(subscriptionKey);
      const isLocallySubscribed = localSubscriptionStatus === 'true';
      
      console.log('💾 [초기화] localStorage 구독 상태:', isLocallySubscribed);
      console.log('💾 [초기화] 키:', subscriptionKey);
      
      // localStorage에 구독 상태가 있으면 백엔드와 동기화 확인
      if (isLocallySubscribed) {
        console.log('🔄 [초기화] localStorage에 구독 상태 있음 - 백엔드 동기화 확인 중...');
        
        try {
          const userId = this.getUserId();
          const baseUrl = getSafeApiUrl('NOTIFICATION');
          const apiUrl = `${baseUrl}/push/subscriptions/${userId}/${deviceId}`;
          
          const headers = this.getAuthHeaders();
          if (storeId) {
            headers['X-Store-Id'] = storeId.toString();
          }
          
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: headers
          });
          
          if (response.ok) {
            const result = await response.json();
            const isBackendSubscribed = result.isSubscribed;
            
            console.log('🔍 [초기화] 백엔드 구독 상태:', isBackendSubscribed);
            
            // 백엔드와 localStorage 동기화
            if (!isBackendSubscribed) {
              console.warn('⚠️ [초기화] 백엔드에서 구독 비활성화 감지 - 다른 기기에서 삭제됨');
              
              // localStorage 업데이트
              localStorage.setItem(subscriptionKey, 'false');
              
              // 브라우저 PushSubscription도 해제
              try {
                if ('serviceWorker' in navigator) {
                  const registration = await navigator.serviceWorker.ready;
                  const subscription = await registration.pushManager?.getSubscription();
                  if (subscription) {
                    await subscription.unsubscribe();
                    console.log('✅ [초기화] 브라우저 PushSubscription 해제 완료');
                  }
                }
              } catch (unsubError) {
                console.error('❌ [초기화] 브라우저 구독 해제 실패:', unsubError);
              }
              
              // UI 상태 업데이트
              this.updateSubscriptionState(false);
              console.log('✅ [초기화] 다른 기기에서 삭제됨 - 구독 상태 완전히 동기화 완료');
            } else {
              console.log('✅ [초기화] 백엔드와 localStorage 동기화 확인');
              this.updateSubscriptionState(true);
            }
          } else {
            console.warn('⚠️ [초기화] 백엔드 상태 확인 실패 - localStorage 상태 유지');
            this.updateSubscriptionState(isLocallySubscribed);
          }
        } catch (error) {
          console.error('❌ [초기화] 백엔드 동기화 실패:', error);
          // 오류 시 localStorage 상태 유지
          this.updateSubscriptionState(isLocallySubscribed);
        }
      } else {
        console.log('ℹ️ [초기화] localStorage에 구독 상태 없음 - UI 비활성화');
        this.updateSubscriptionState(false);
      }
    } catch (error) {
      console.error('❌ [초기화] 구독 상태 복원 실패:', error);
      this.updateSubscriptionState(false);
    }
  }

  private getUserId(): string {
    // 사용자 ID 가져오기 (실제 인증 시스템에 맞게 수정 필요)
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      if (parsed.userId || parsed.id) {
        console.log('🔍 [사용자 ID] user_info에서 사용자 ID 발견:', parsed.userId || parsed.id);
        return (parsed.userId || parsed.id).toString();
      }
    }

    // 토큰에서 사용자 ID 추출 시도
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.id) {
          console.log('🔍 [사용자 ID] JWT 토큰에서 사용자 ID 발견:', payload.id);
          return payload.id.toString();
        }
      } catch (error) {
        console.error('❌ [사용자 ID] JWT 토큰 파싱 실패:', error);
      }
    }

    console.warn('⚠️ [사용자 ID] 사용자 ID를 찾을 수 없어 default_user 사용');
    return 'default_user';
  }

  private getStoreId(): number {
    // StoreId 가져오기
    const storeId = localStorage.getItem('storeId');
    if (storeId) {
      const parsedStoreId = parseInt(storeId);
      if (!isNaN(parsedStoreId)) {
        console.log('🏪 [매장 ID] localStorage에서 StoreId 발견:', parsedStoreId);
        return parsedStoreId;
      }
    }

    // 토큰에서 StoreId 추출 시도
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.storeId) {
          const parsedStoreId = parseInt(payload.storeId);
          if (!isNaN(parsedStoreId)) {
            console.log('🏪 [매장 ID] JWT 토큰에서 StoreId 발견:', parsedStoreId);
            return parsedStoreId;
          }
        }
      } catch (error) {
        console.error('❌ [매장 ID] JWT 토큰 파싱 실패:', error);
      }
    }

    console.warn('⚠️ [매장 ID] StoreId를 찾을 수 없어 0 사용');
    return 0;
  }

  // 기기별 고유 식별자 생성/조회
  private getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('deviceId', deviceId);
      console.log('🆔 [기기 ID] 새 기기 ID 생성:', deviceId);
    } else {
      console.log('🆔 [기기 ID] 기존 기기 ID 사용:', deviceId);
    }
    return deviceId;
  }

  // 기기별 구독 상태 저장
  private saveDeviceSubscriptionStatus(isEnabled: boolean): void {
    const deviceId = this.getDeviceId();
    const storeId = this.getStoreId();
    const key = `subscription_${storeId}_${deviceId}`;
    localStorage.setItem(key, isEnabled.toString());
    console.log(`💾 [기기별 저장] 구독 상태 저장: ${key} = ${isEnabled}`);
  }

  // 기기별 구독 상태 조회
  private getDeviceSubscriptionStatus(): boolean {
    const deviceId = this.getDeviceId();
    const storeId = this.getStoreId();
    const key = `subscription_${storeId}_${deviceId}`;
    const status = localStorage.getItem(key);
    const isEnabled = status === 'true';
    console.log(`🔍 [기기별 조회] 구독 상태 조회: ${key} = ${isEnabled}`);
    return isEnabled;
  }

  // 푸시 구독 등록
  async subscribe(
    subscriptionData: PushSubscriptionData,
    settings: NotificationSettings,
    forceRefresh: boolean = false,
    skipUnsubscribe: boolean = false
  ): Promise<boolean> {
    // 순수 모바일이 아니면 즉시 종료
    if (!this.isPureMobile()) {
      console.log('🚫 [구독] 순수 모바일이 아님 - 구독 차단');
      return false;
    }

    try {
      console.log('🚀 [구독] 구독 등록 시작');
      
      // 0. 구독 전에 기존 UNREGISTERED 토큰 정리 (안정적 복구)
      if (!skipUnsubscribe) {
        try {
          const status = await this.checkAndRestoreSubscription();
          if (status.needsTokenRefresh) {
            console.log('🔄 [구독] UNREGISTERED 토큰 감지 - 기존 구독 삭제 후 재구독');
            // 기존 구독을 완전히 삭제하여 무효 토큰 제거
            try {
              await this.unsubscribe();
              console.log('✅ [구독] UNREGISTERED 토큰 정리 완료');
              // DB 업데이트 시간 확보 (안정성 향상)
              await new Promise(resolve => setTimeout(resolve, 500));
            } catch (unsubscribeError) {
              console.warn('⚠️ [구독] UNREGISTERED 토큰 정리 실패 (계속 진행):', unsubscribeError);
            }
          }
        } catch (statusCheckError) {
          console.warn('⚠️ [구독] 기존 구독 상태 확인 실패 (계속 진행):', statusCheckError);
        }
      }
      
      // 1. 기존 구독 해제 (중복 방지)
      if (!skipUnsubscribe) {
        console.log('🔄 [구독] 기존 구독 해제 중...');
        try {
          await this.unsubscribe();
          console.log('✅ [구독] 기존 구독 해제 완료');
        } catch (unsubscribeError) {
          console.warn('⚠️ [구독] 기존 구독 해제 실패 (계속 진행):', unsubscribeError);
        }
      } else {
        console.log('⏭️ [구독] 기존 구독 해제 생략 (토큰 갱신 전용)');
      }
      
      // iOS Safari 전용 디버깅 정보 수집
      if (this.isIOSSafari()) {
        const debugInfo = this.collectIOSSafariDebugInfo();
        console.log('🍎 [iOS Safari] 디버깅 정보:', debugInfo);
      }
      
      // 플랫폼별 푸시알림 지원 확인
      if (!this.isPushNotificationSupported()) {
        console.log('❌ [구독] 현재 플랫폼에서 푸시알림을 지원하지 않습니다');
        
        // iOS Safari 전용 지원 확인 실패 로깅
        if (this.isIOSSafari()) {
          console.error('🍎 [iOS Safari] 푸시알림 지원 확인 실패 - 상세 정보:', {
            hasServiceWorker: 'serviceWorker' in navigator,
            hasPushManager: 'PushManager' in window,
            hasNotification: 'Notification' in window,
            isSecureContext: window.isSecureContext,
            protocol: window.location.protocol
          });
        }
        
        return false;
      }
      
      const userId = this.getUserId();
      console.log('📋 [구독] 사용자 ID:', userId);
      
      const fcmToken: string | null = USE_FCM ? localStorage.getItem('fcm_token') : null;
      
      const deviceId = this.getDeviceId();
      
      // 프론트엔드 형식을 백엔드 형식으로 변환
      const backendSettings = {
        dailyReportEnabled: settings.dailyReport,
        inboundReminderEnabled: settings.inboundReminder,
        realtimeLowStockEnabled: settings.realtimeLowStock,
        newMenuEnabled: settings.newMenu,
        orderDeadlineEnabled: settings.orderDeadline,
        // 하위 호환성
        lowStockEnabled: settings.realtimeLowStock ?? settings.lowStock ?? true,
        orderAvailableEnabled: settings.orderAvailable ?? true,
        inboundCompleteEnabled: settings.inboundComplete ?? true,
        systemNotificationEnabled: settings.system ?? true
      };
      
      const request: SubscribeRequest = {
        deviceId: deviceId,
        endpoint: subscriptionData.endpoint,
        p256dh: subscriptionData.keys.p256dh,
        auth: subscriptionData.keys.auth,
        settings: backendSettings as any,
        fcmToken: fcmToken || undefined
      };
      console.log('📤 [구독] 요청 데이터 (DeviceId: {deviceId}):', request);

      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      
      const apiUrl = `${baseUrl}/push/subscribe/${userId}`;
      console.log('🔗 [구독] API URL:', apiUrl);

      // StoreId 헤더 추가
      const headers = this.getAuthHeaders();
      const storeId = this.getStoreId();
      if (storeId) {
        headers['X-Store-Id'] = storeId.toString();
        console.log('🏪 [구독] StoreId 헤더 추가:', storeId);
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(request)
      });

      console.log('📨 [구독] 응답 상태 코드:', response.status);
      console.log('📨 [구독] 응답 상태 텍스트:', response.statusText);

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('❌ [구독] 응답 본문:', errorText);
        } catch (textError) {
          console.error('❌ [구독] 응답 본문 읽기 실패:', textError);
        }
        throw new Error(`구독 등록 실패: ${response.status} - ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ [구독] 푸시 구독 등록 성공:', result);
      
      // Endpoint를 Service Worker·localStorage에 저장 (30일 유효, 만료 체크용)
      if (subscriptionData.endpoint) {
        await this.saveEndpointToServiceWorker(subscriptionData.endpoint);
        console.log('💾 [구독] Endpoint 저장 완료 (30일 유효):', subscriptionData.endpoint);
      }
      
      // 전역 상태 업데이트
      this.updateSubscriptionState(true);
      
      // 기기별 구독 상태 저장 (활성화)
      this.saveDeviceSubscriptionStatus(true);
      
      // 사용자가 명시적으로 구독을 활성화했으므로 비활성화 플래그 제거
      localStorage.removeItem('user_disabled_subscription');
      localStorage.removeItem('user_dismissed_renewal'); // "더 이상 표시 안함" 플래그 제거
      console.log('✅ [구독] 사용자 비활성화 플래그 제거');
      
      // 만료 알림 플래그 초기화 (재구독 성공 시)
      localStorage.removeItem('lastEndpointExpiredTime');
      localStorage.removeItem('lastEndpointWarningTime');
      console.log('✅ [구독] 만료 알림 플래그 초기화');
      
      return true;
    } catch (error) {
      console.error('❌ [구독] 푸시 구독 등록 중 오류:', error);
      
      // iOS Safari 전용 오류 로깅
      if (this.isIOSSafari()) {
        const debugInfo = this.collectIOSSafariDebugInfo();
        console.error('🍎 [iOS Safari] 구독 실패 상세 정보:', {
          error: error instanceof Error ? error.message : String(error),
          debugInfo,
          subscriptionData,
          settings,
          forceRefresh
        });
        
        // iOS Safari 전용 오류 전송 (선택적)
        try {
          await this.sendIOSSafariErrorReport(error, 'subscribe', debugInfo);
        } catch (reportError) {
          console.warn('⚠️ [iOS Safari] 오류 리포트 전송 실패:', reportError);
        }
      }
      
      return false;
    }
  }

  // Service Worker에 Endpoint 저장 (iOS 호환 + 만료 시간 포함)
  private async saveEndpointToServiceWorker(endpoint: string): Promise<boolean> {
    try {
      // Endpoint 데이터에 만료 시간 추가 (30일)
      const endpointData = {
        value: endpoint,
        expires: Date.now() + (ENDPOINT_VALID_DAYS * 24 * 60 * 60 * 1000), // 30일
        createdAt: Date.now()
      };

      // iOS Safari 호환성 체크
      if (!navigator.serviceWorker) {
        console.warn('⚠️ [iOS] Service Worker 미지원 - localStorage 사용');
        localStorage.setItem('lastPushEndpoint', JSON.stringify(endpointData));
        return true;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // iOS Safari에서 MessageChannel이 제한될 수 있으므로 단순한 방식 사용
      if (registration.active) {
        registration.active.postMessage({
          type: 'SAVE_ENDPOINT',
          endpoint: endpoint,
          expires: endpointData.expires
        });
        
        // iOS에서는 응답을 기다리지 않고 localStorage에도 백업 저장
        localStorage.setItem('lastPushEndpoint', JSON.stringify(endpointData));
        console.log('✅ [iOS 호환] Endpoint 저장 완료 (Service Worker + localStorage, 만료:', new Date(endpointData.expires).toLocaleString(), ')');
        return true;
      } else {
        // Service Worker가 활성화되지 않은 경우 localStorage만 사용
        localStorage.setItem('lastPushEndpoint', JSON.stringify(endpointData));
        console.log('✅ [iOS 호환] Endpoint 저장 완료 (localStorage만, 만료:', new Date(endpointData.expires).toLocaleString(), ')');
        return true;
      }
    } catch (error) {
      console.error('❌ [iOS] Service Worker Endpoint 저장 실패:', error);
      // 에러 시 localStorage로 fallback
      const endpointData = {
        value: endpoint,
        expires: Date.now() + (ENDPOINT_VALID_DAYS * 24 * 60 * 60 * 1000),
        createdAt: Date.now()
      };
      localStorage.setItem('lastPushEndpoint', JSON.stringify(endpointData));
      return true;
    }
  }

  // Service Worker에서 Endpoint 조회 (iOS 호환 + 만료 체크)
  private async getEndpointFromServiceWorker(): Promise<string | null> {
    try {
      // iOS Safari 호환성 체크
      if (!navigator.serviceWorker) {
        console.warn('⚠️ [iOS] Service Worker 미지원 - localStorage에서 조회');
        return this.getValidEndpointFromLocalStorage();
      }

      const registration = await navigator.serviceWorker.ready;
      
      // iOS Safari에서는 MessageChannel 대신 단순한 방식 사용
      if (registration.active) {
        // iOS에서는 Service Worker 응답을 기다리지 않고 localStorage에서 조회
        const validEndpoint = this.getValidEndpointFromLocalStorage();
        if (validEndpoint) {
          console.log('✅ [iOS 호환] localStorage에서 유효한 Endpoint 조회:', validEndpoint);
          return validEndpoint;
        }
        
        // localStorage에 없거나 만료된 경우 Service Worker에 요청 (응답 기다리지 않음)
        registration.active.postMessage({
          type: 'GET_ENDPOINT'
        });
        
        console.log('⚠️ [iOS] Service Worker에 Endpoint 조회 요청 (응답 대기 안함)');
        return null;
      } else {
        // Service Worker가 활성화되지 않은 경우 localStorage에서 조회
        const validEndpoint = this.getValidEndpointFromLocalStorage();
        console.log('✅ [iOS 호환] localStorage에서 Endpoint 조회 (SW 비활성):', validEndpoint);
        return validEndpoint;
      }
    } catch (error) {
      console.error('❌ [iOS] Service Worker Endpoint 조회 실패:', error);
      // 에러 시 localStorage에서 조회
      return this.getValidEndpointFromLocalStorage();
    }
  }

  // localStorage에서 유효한 Endpoint 조회 (만료 체크 포함)
  private getValidEndpointFromLocalStorage(): string | null {
    try {
      const endpointDataStr = localStorage.getItem('lastPushEndpoint');
      if (!endpointDataStr) {
        return null;
      }

      // 기존 형식 (문자열)인 경우 그대로 반환 (하위 호환성)
      if (!endpointDataStr.startsWith('{')) {
        console.log('🔄 [만료 체크] 기존 형식 Endpoint 발견 (만료 체크 안함):', endpointDataStr);
        return endpointDataStr;
      }

      // 새로운 형식 (객체)인 경우 만료 체크
      const endpointData = JSON.parse(endpointDataStr);
      const now = Date.now();
      const timeUntilExpiry = endpointData.expires - now;
      const daysUntilExpiry = Math.round(timeUntilExpiry / (1000 * 60 * 60 * 24));
      
      if (endpointData.expires && now > endpointData.expires) {
        // 만료된 경우
        console.warn('⚠️ [만료 체크] Endpoint가 만료됨:', {
          endpoint: endpointData.value,
          만료시간: new Date(endpointData.expires).toLocaleString(),
          현재시간: new Date(now).toLocaleString()
        });
        
        // 만료된 데이터 삭제
        localStorage.removeItem('lastPushEndpoint');
        
        // 사용자에게 갱신 알림 표시
        this.showEndpointExpiredNotification();
        
        return null;
      } else if (endpointData.expires && timeUntilExpiry <= (ENDPOINT_REFRESH_BEFORE_DAYS * 24 * 60 * 60 * 1000)) {
        // 만료 15일 전: 자동 갱신 시도 (12시간 내 중복 방지)
        const lastAttempt = localStorage.getItem('lastEndpointRefreshAttempt');
        const now = Date.now();
        if (!lastAttempt || now - parseInt(lastAttempt, 10) >= ENDPOINT_REFRESH_COOLDOWN_MS) {
          localStorage.setItem('lastEndpointRefreshAttempt', now.toString());
          this.requestAutoResubscribe('endpoint-15day-refresh').catch(() => {});
        }
        console.warn('⚠️ [만료 체크] Endpoint 만료 임박 (15일 전):', {
          endpoint: endpointData.value,
          만료까지: daysUntilExpiry + '일',
          만료시간: new Date(endpointData.expires).toLocaleString()
        });
        this.showEndpointExpiringSoonNotification(daysUntilExpiry);
        return endpointData.value;
      }

      // 유효한 경우
      console.log('✅ [만료 체크] Endpoint 유효:', {
        endpoint: endpointData.value,
        만료까지: Math.round((endpointData.expires - now) / (1000 * 60 * 60 * 24)) + '일'
      });
      
      return endpointData.value;
    } catch (error) {
      console.error('❌ [만료 체크] Endpoint 데이터 파싱 실패:', error);
      return null;
    }
  }

  // Endpoint 만료 임박 시 사용자 알림 표시
  private showEndpointExpiringSoonNotification(daysUntilExpiry: number): void {
    // 사용자가 명시적으로 구독을 비활성화한 경우 알림 표시 안함
    const userDisabled = localStorage.getItem('user_disabled_subscription');
    if (userDisabled === 'true') {
      console.log('🚫 [만료 임박 알림] 사용자가 알림을 비활성화함 - 알림 표시 안함');
      return;
    }
    
    // 중복 알림 방지 (24시간 내에 이미 알림을 보냈는지 체크)
    const lastWarningKey = 'lastEndpointWarningTime';
    const lastWarningTime = localStorage.getItem(lastWarningKey);
    const now = Date.now();
    
    if (lastWarningTime && (now - parseInt(lastWarningTime)) < (24 * 60 * 60 * 1000)) {
      console.log('🔄 [만료 임박 알림] 24시간 내 이미 알림 발송됨 - 건너뛰기');
      return;
    }
    
    // 마지막 알림 시간 저장
    localStorage.setItem(lastWarningKey, now.toString());

    // 브라우저 알림 권한 확인
    if (Notification.permission === 'granted') {
      const message = daysUntilExpiry <= 0 
        ? '푸시 알림 설정이 오늘 만료됩니다. 지금 갱신해주세요.'
        : `푸시 알림 설정이 ${daysUntilExpiry}일 후 만료됩니다. 미리 갱신해주세요.`;

      const notification = new Notification('⏰ 푸시 알림 만료 임박', {
        body: message,
        icon: '/Icon.svg',
        badge: '/Icon.svg',
        tag: 'endpoint-expiring-soon',
        requireInteraction: false // 만료 임박은 덜 강제적
      } as NotificationOptions);

      // 알림 클릭 시 설정 페이지로 이동
      notification.onclick = () => {
        window.location.href = '/settings';
        notification.close();
      };

      // 알림 에러 처리
      notification.onerror = (error) => {
        console.error('❌ [만료 임박 알림] 브라우저 알림 표시 실패:', error);
      };
    }

    // 토스트 알림도 표시 (앱 내부)
    if (typeof window !== 'undefined' && (window as any).showToast) {
      (window as any).showToast({
        type: 'info',
        title: '푸시 알림 만료 임박',
        message: daysUntilExpiry <= 0 
          ? '푸시 알림 설정이 오늘 만료됩니다. 지금 갱신해주세요.'
          : `푸시 알림 설정이 ${daysUntilExpiry}일 후 만료됩니다.`,
        duration: 8000,
        action: {
          label: '지금 갱신',
          onClick: () => {
            window.location.href = '/settings';
          }
        },
        secondaryAction: {
          label: '더 이상 표시 안함',
          onClick: () => {
            localStorage.setItem('user_disabled_subscription', 'true');
            localStorage.setItem('user_dismissed_renewal', 'true');
            localStorage.removeItem('lastEndpointWarningTime'); // 플래그 초기화
            console.log('🚫 [만료 임박 알림] 사용자가 "더 이상 표시 안함" 선택 - 알림 비활성화');
          }
        }
      });
    }

    console.log(`⏰ [만료 임박 알림] 사용자에게 ${daysUntilExpiry}일 후 만료 알림 표시`);
  }

  // Endpoint 만료 시 사용자 알림 표시
  private showEndpointExpiredNotification(): void {
    // 사용자가 명시적으로 구독을 비활성화한 경우 알림 표시 안함
    const userDisabled = localStorage.getItem('user_disabled_subscription');
    if (userDisabled === 'true') {
      console.log('🚫 [만료 알림] 사용자가 알림을 비활성화함 - 알림 표시 안함');
      return;
    }
    
    // 중복 알림 방지 (1시간 내에 이미 알림을 보냈는지 체크)
    const lastExpiredKey = 'lastEndpointExpiredTime';
    const lastExpiredTime = localStorage.getItem(lastExpiredKey);
    const now = Date.now();
    
    if (lastExpiredTime && (now - parseInt(lastExpiredTime)) < (60 * 60 * 1000)) {
      console.log('🔄 [만료 알림] 1시간 내 이미 알림 발송됨 - 건너뛰기');
      return;
    }
    
    // 마지막 알림 시간 저장
    localStorage.setItem(lastExpiredKey, now.toString());
    
    // 브라우저 알림 권한 확인
    if (Notification.permission === 'granted') {
      const notification = new Notification('🔄 푸시 알림 갱신 필요', {
        body: '푸시 알림 설정이 만료되었습니다. 설정 페이지에서 다시 구독해주세요.',
        icon: '/Icon.svg',
        badge: '/Icon.svg',
        tag: 'endpoint-expired',
        requireInteraction: true
      } as NotificationOptions);

      // 알림 클릭 시 설정 페이지로 이동
      notification.onclick = () => {
        window.location.href = '/settings';
        notification.close();
      };

      // 알림 에러 처리
      notification.onerror = (error) => {
        console.error('❌ [만료 알림] 브라우저 알림 표시 실패:', error);
      };
    }

    // 토스트 알림도 표시 (앱 내부)
    if (typeof window !== 'undefined' && (window as any).showToast) {
      (window as any).showToast({
        type: 'warning',
        title: '푸시 알림 갱신 필요',
        message: '푸시 알림 설정이 만료되었습니다. 설정에서 다시 구독해주세요.',
        duration: 10000,
        action: {
          label: '설정으로 이동',
          onClick: () => {
            window.location.href = '/settings';
          }
        },
        secondaryAction: {
          label: '더 이상 표시 안함',
          onClick: () => {
            localStorage.setItem('user_disabled_subscription', 'true');
            localStorage.setItem('user_dismissed_renewal', 'true');
            localStorage.removeItem('lastEndpointExpiredTime'); // 플래그 초기화
            console.log('🚫 [만료 알림] 사용자가 "더 이상 표시 안함" 선택 - 알림 비활성화');
          }
        }
      });
    }

    console.log('🔄 [만료 알림] 사용자에게 Endpoint 갱신 알림 표시');
  }

  // 토큰만 업데이트 (unsubscribe 없이, UNREGISTERED 복구 전용)
  private async updateTokenOnly(newToken: string, subscriptionData: PushSubscriptionData, settings: NotificationSettings): Promise<boolean> {
    if (!USE_FCM) {
      return false;
    }
    try {
      console.log('🔄 [토큰 업데이트] 토큰만 업데이트 시작 (구독 해제 없음)');
      
      const userId = this.getUserId();
      const deviceId = this.getDeviceId();
      
      // 요청 데이터 생성
      const request = {
        deviceId: deviceId,
        endpoint: subscriptionData.endpoint,
        p256dh: subscriptionData.keys.p256dh,
        auth: subscriptionData.keys.auth,
        settings: settings,
        fcmToken: newToken
      };
      
      console.log('📤 [토큰 업데이트] 요청 데이터 (DeviceId: {deviceId}):', request);
      
      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      const apiUrl = `${baseUrl}/push/subscribe/${userId}`;
      console.log('🔗 [토큰 업데이트] API URL:', apiUrl);
      
      const headers = this.getAuthHeaders();
      const storeId = this.getStoreId();
      if (storeId) {
        headers['X-Store-Id'] = storeId.toString();
        console.log('🏪 [토큰 업데이트] StoreId 헤더 추가:', storeId);
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(request)
      });
      
      if (!response.ok) {
        throw new Error(`토큰 업데이트 실패: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ [토큰 업데이트] 토큰만 업데이트 성공:', result);
      
      return true;
    } catch (error) {
      console.error('❌ [토큰 업데이트] 토큰 업데이트 중 오류:', error);
      return false;
    }
  }

  // 자동 토큰 갱신 (UNREGISTERED 복구용)
  async autoRefreshToken(): Promise<boolean> {
    if (!USE_FCM) {
      return false;
    }
    try {
      // 중복 실행 방지
      if (this.isAutoRefreshing) {
        console.log('🔄 [자동 토큰 갱신] 이미 갱신 중 - 건너뛰기');
        return false;
      }
      
      this.isAutoRefreshing = true;
      console.log('🔄 [자동 토큰 갱신] UNREGISTERED 복구 시작');
      
      // 기기별 구독 상태 확인
      const deviceSubscriptionStatus = this.getDeviceSubscriptionStatus();
      if (!deviceSubscriptionStatus) {
        console.log('🚫 [자동 토큰 갱신] 기기별 구독 비활성화됨 - 갱신 불필요');
        this.isAutoRefreshing = false;
        return false;
      }
      
      // Service Worker 등록 확인
      if (!navigator.serviceWorker) {
        console.warn('⚠️ [자동 토큰 갱신] Service Worker 미지원');
        this.isAutoRefreshing = false;
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // 기존 브라우저 구독 확인
      const existingSubscription = await registration.pushManager?.getSubscription();
      if (!existingSubscription) {
        console.warn('⚠️ [자동 토큰 갱신] 브라우저 구독 없음 - 갱신 불가능');
        this.isAutoRefreshing = false;
        return false;
      }

      console.log('✅ [자동 토큰 갱신] 브라우저 구독 정보 확인 완료');

      // 새로운 FCM 토큰 생성
      const newToken = '';

      // 구독 데이터 준비
      const p256dhKey = existingSubscription.getKey('p256dh');
      const authKey = existingSubscription.getKey('auth');
      
      if (!p256dhKey || !authKey) {
        console.error('❌ [자동 토큰 갱신] 구독 키 없음');
        this.isAutoRefreshing = false;
        return false;
      }

      const subscriptionData = {
        endpoint: existingSubscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(p256dhKey),
          auth: this.arrayBufferToBase64(authKey)
        },
        fcmToken: newToken
      };

      // 저장된 설정 조회
      const settingsStr = localStorage.getItem('notificationSettings');
      const settings: NotificationSettings = settingsStr ? JSON.parse(settingsStr) : {
        dailyReport: true,
        inboundReminder: true,
        realtimeLowStock: true,
        newMenu: true,
        orderDeadline: true,
        // 하위 호환성
        lowStock: true,
        orderAvailable: true,
        inboundComplete: true,
        system: true
      };

      // 백엔드에 토큰만 업데이트 (unsubscribe 없이)
      console.log('🔄 [자동 토큰 갱신] 백엔드에 토큰만 업데이트 시작...');
      const success = await this.updateTokenOnly(newToken, subscriptionData, settings);

      if (success) {
        console.log('✅ [자동 토큰 갱신] UNREGISTERED 자동 복구 완료 - 알림 정상화');
        this.isAutoRefreshing = false;
        return true;
      } else {
        console.error('❌ [자동 토큰 갱신] 토큰 업데이트 실패');
        this.isAutoRefreshing = false;
        return false;
      }
    } catch (error) {
      console.error('❌ [자동 토큰 갱신] 자동 갱신 중 오류:', error);
      this.isAutoRefreshing = false;
      return false;
    }
  }

  // 푸시 구독 해제
  async unsubscribe(): Promise<boolean> {
    // 순수 모바일이 아니면 즉시 종료
    if (!this.isPureMobile()) {
      console.log('🚫 [구독 해제] 순수 모바일이 아님 - 구독 해제 차단');
      return false;
    }

    try {
      const userId = this.getUserId();
      const deviceId = this.getDeviceId();
      const storeId = this.getStoreId();

      console.log('🚀 [구독 해제] 시작 - UserId: {userId}, DeviceId: {deviceId}, StoreId: {storeId}');

      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      const apiUrl = `${baseUrl}/push/unsubscribe/${userId}/${deviceId}`;
      
      console.log('🔗 [구독 해제] API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`구독 해제 실패: ${response.status}`);
      }

      const result = await response.json();
      console.log('푸시 구독 해제 성공:', result);
      
      // 전역 상태 업데이트
      this.updateSubscriptionState(false);
      
      // 기기별 구독 상태 저장 (비활성화)
      this.saveDeviceSubscriptionStatus(false);
      
      // 사용자가 명시적으로 구독을 비활성화했음을 표시
      localStorage.setItem('user_disabled_subscription', 'true');
      console.log('🚫 [구독 해제] 사용자 비활성화 플래그 설정');
      
      // 만료 알림 플래그 초기화 (구독 해제 시)
      localStorage.removeItem('lastEndpointExpiredTime');
      localStorage.removeItem('lastEndpointWarningTime');
      localStorage.removeItem('user_dismissed_renewal'); // "더 이상 표시 안함" 플래그 제거
      console.log('✅ [구독 해제] 만료 알림 플래그 초기화');
      
      return true;
    } catch (error) {
      console.error('푸시 구독 해제 중 오류:', error);
      return false;
    }
  }

  // 알림 설정 업데이트
  async getSettings(): Promise<NotificationSettings | null> {
    try {
      const userId = this.getUserId();
      if (!userId) {
        console.warn('⚠️ [설정 조회] UserId가 없음');
        return null;
      }

      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');

      const response = await fetch(`${baseUrl}/push/settings/${userId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('⚠️ [설정 조회] 활성 구독이 없음 - 기본값 반환');
          // 기본값 반환
          return {
            dailyReport: true,
            inboundReminder: true,
            realtimeLowStock: true,
            newMenu: true,
            orderDeadline: true
          };
        }
        throw new Error(`설정 조회 실패: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [설정 조회] 성공:', data);
      
      // 백엔드 응답을 프론트엔드 형식으로 변환
      const settings: NotificationSettings = {
        dailyReport: data.dailyReportEnabled ?? true,
        inboundReminder: data.inboundReminderEnabled ?? true,
        realtimeLowStock: data.realtimeLowStockEnabled ?? true,
        newMenu: data.newMenuEnabled ?? true,
        orderDeadline: data.orderDeadlineEnabled ?? true,
        // 하위 호환성
        lowStock: data.realtimeLowStockEnabled ?? data.lowStockEnabled ?? true,
        orderAvailable: data.orderAvailableEnabled ?? true,
        inboundComplete: data.inboundCompleteEnabled ?? true,
        system: data.systemNotificationEnabled ?? true
      };
      
      return settings;
    } catch (error) {
      console.error('❌ [설정 조회] 오류:', error);
      // 오류 시 기본값 반환
      return {
        dailyReport: true,
        inboundReminder: true,
        realtimeLowStock: true,
        newMenu: true,
        orderDeadline: true
      };
    }
  }

  async updateSettings(settings: NotificationSettings): Promise<boolean> {
    // 순수 모바일이 아니면 즉시 종료
    if (!this.isPureMobile()) {
      console.log('🚫 [설정 업데이트] 순수 모바일이 아님 - 설정 업데이트 차단');
      return false;
    }

    try {
      const userId = this.getUserId();
      console.log('🔄 [설정 업데이트] 시작 - UserId:', userId);
      console.log('🔄 [설정 업데이트] 프론트엔드 설정:', settings);
      
      // 프론트엔드 형식을 백엔드 형식으로 변환
      const backendSettings = {
        dailyReportEnabled: settings.dailyReport,
        inboundReminderEnabled: settings.inboundReminder,
        realtimeLowStockEnabled: settings.realtimeLowStock,
        newMenuEnabled: settings.newMenu,
        orderDeadlineEnabled: settings.orderDeadline,
        // 하위 호환성
        lowStockEnabled: settings.realtimeLowStock ?? settings.lowStock ?? true,
        orderAvailableEnabled: settings.orderAvailable ?? true,
        inboundCompleteEnabled: settings.inboundComplete ?? true,
        systemNotificationEnabled: settings.system ?? true
      };
      
      console.log('🔄 [설정 업데이트] 백엔드 형식 변환 완료:', backendSettings);
      
      const request: UpdateSettingsRequest = { settings: backendSettings as any };

      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      const url = `${baseUrl}/push/settings/${userId}`;
      
      console.log('🔄 [설정 업데이트] API 요청 시작 - URL:', url);
      console.log('🔄 [설정 업데이트] 요청 본문:', JSON.stringify(request, null, 2));

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      });

      console.log('🔄 [설정 업데이트] API 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [설정 업데이트] API 응답 실패:', errorText);
        throw new Error(`설정 업데이트 실패: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ [설정 업데이트] 성공 - 응답:', result);
      return true;
    } catch (error) {
      console.error('❌ [설정 업데이트] 오류:', error);
      return false;
    }
  }

  // 테스트 알림 전송
  async sendTestNotification(userId?: string): Promise<void> {
    try {
      console.log('🚀 [푸시 알림] 테스트 알림 전송 시작');
      
      // 사용자 ID가 제공되지 않으면 현재 사용자 ID 사용
      const actualUserId = userId || this.getUserId();
      console.log('📋 [푸시 알림] 사용자 ID:', actualUserId);
      console.log('🌍 [푸시 알림] 현재 환경:', isDevelopment() ? 'development' : 'production');
      
      // 환경 설정에서 API URL 가져오기
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      
      const apiUrl = `${baseUrl}/push/test/${actualUserId}`;
      console.log('🔗 [푸시 알림] API URL:', apiUrl);
      
      const requestBody = {
        title: '테스트 알림',
        body: '푸시 알림이 정상적으로 작동합니다!',
        type: 'system'
      };
      console.log('📤 [푸시 알림] 요청 데이터:', requestBody);
      
      console.log('🌐 [푸시 알림] 네트워크 요청 시작...');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      console.log('📨 [푸시 알림] 응답 상태 코드:', response.status);
      console.log('📨 [푸시 알림] 응답 상태 텍스트:', response.statusText);
      console.log('📨 [푸시 알림] 응답 헤더:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        console.error('❌ [푸시 알림] HTTP 오류 발생');
        console.error('❌ [푸시 알림] 상태 코드:', response.status);
        console.error('❌ [푸시 알림] 상태 메시지:', response.statusText);
        
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('❌ [푸시 알림] 응답 본문:', errorText);
        } catch (textError) {
          console.error('❌ [푸시 알림] 응답 본문 읽기 실패:', textError);
        }
        
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText || response.statusText}`);
      }

      console.log('✅ [푸시 알림] 응답 수신 성공, JSON 파싱 시작...');
      const result = await response.json();
      console.log('✅ [푸시 알림] 테스트 알림 전송 성공:', result);
      
    } catch (error) {
      console.error('❌ [푸시 알림] 테스트 알림 전송 중 오류:', error);
      console.error('❌ [푸시 알림] 오류 타입:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('❌ [푸시 알림] 오류 메시지:', error instanceof Error ? error.message : String(error));
      console.error('❌ [푸시 알림] 오류 스택:', error instanceof Error ? error.stack : 'No stack available');
      
      // 백엔드 서버가 실행되지 않은 경우 사용자에게 친화적인 메시지 제공
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('🔌 [푸시 알림] 네트워크 연결 실패 - 백엔드 서버 미실행 추정');
        throw new Error('백엔드 서버가 실행되지 않았습니다. InventoryManagement 서비스(포트 5001)를 시작한 후 다시 시도해주세요.');
      }
      
      if (error instanceof Error && error.message.includes('404')) {
        console.error('🚫 [푸시 알림] API 엔드포인트를 찾을 수 없음');
        throw new Error('푸시 알림 API 엔드포인트가 존재하지 않습니다. 백엔드 서버에 /api/push/test 엔드포인트가 구현되어 있는지 확인해주세요.');
      }
      
      // FCM 토큰 무효화 오류 감지
      if (USE_FCM && error instanceof Error && error.message.includes('UNREGISTERED')) {
        throw new Error('FCM 토큰이 무효화되었습니다. 페이지를 새로고침하고 알림을 다시 활성화해주세요.');
      }
      
      throw error;
    }
  }

  // 매장의 모든 활성 구독 기기 목록 조회
  async getStoreActiveDevices(storeId: number, diagnose: boolean = true): Promise<any[] | null> {
    try {
      console.log('🔍 [기기 목록] 매장 기기 목록 조회 시작 - StoreId:', storeId);
      
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      const query = diagnose ? '?diagnose=true' : '';
      const apiUrl = `${baseUrl}/push/devices/store/${storeId}${query}`;
      console.log('🔗 [기기 목록] API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        console.error('❌ [기기 목록] 조회 실패:', response.status);
        return null;
      }
      
      const result = await response.json();
      console.log('✅ [기기 목록] 조회 성공:', result);
      
      return result.devices || [];
    } catch (error) {
      console.error('❌ [기기 목록] 조회 중 오류:', error);
      return null;
    }
  }

  // 특정 기기 구독 삭제
  async deleteDeviceSubscription(userId: string, storeId: number, deviceId: string): Promise<boolean> {
    try {
      console.log('🗑️ [기기 삭제] 삭제 시작 - DeviceId:', deviceId);
      
      // 현재 기기는 삭제 불가
      const currentDeviceId = this.getDeviceId();
      if (deviceId === currentDeviceId) {
        console.warn('⚠️ [기기 삭제] 현재 기기는 삭제할 수 없습니다');
        alert('현재 사용 중인 기기는 삭제할 수 없습니다.\n알림 비활성화 버튼을 사용해주세요.');
        return false;
      }
      
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      const apiUrl = `${baseUrl}/push/devices/${userId}/${storeId}/${deviceId}`;
      console.log('🔗 [기기 삭제] API URL:', apiUrl);
      
      const headers = this.getAuthHeaders();
      // 요청하는 기기의 DeviceId를 헤더에 추가
      headers['X-Device-Id'] = currentDeviceId;
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: headers
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [기기 삭제] 삭제 실패:', response.status, errorData);
        alert(errorData.message || '기기 삭제에 실패했습니다.');
        return false;
      }
      
      const result = await response.json();
      console.log('✅ [기기 삭제] 삭제 성공:', result);
      
      return true;
    } catch (error) {
      console.error('❌ [기기 삭제] 삭제 중 오류:', error);
      alert('기기 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }

  // VAPID 공개키 가져오기
  async getVapidPublicKey(): Promise<string | null> {
    try {
      // 개발 환경에서는 올바른 백엔드 포트 사용
          const baseUrl = isDevelopment()
      ? getSafeApiUrl('INVENTORY') // InventoryManagement 서비스 포트
      : API_BASE_URL;

      const response = await fetch(`${baseUrl}/push/vapid-key`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`VAPID 키 조회 실패: ${response.status}`);
      }

      const result = await response.json();
      return result.publicKey;
    } catch (error) {
      console.error('VAPID 키 조회 중 오류:', error);
      return null;
    }
  }

  // FCM 토큰 유효성 검사
  private isValidFcmToken(token: string): boolean {
    try {
      // FCM 토큰 형식 검사: 길이와 패턴 확인
      if (!token || typeof token !== 'string') {
        return false;
      }
      
      // FCM 토큰은 보통 140-180자 정도의 길이
      if (token.length < 100 || token.length > 200) {
        console.warn('⚠️ [FCM 토큰 검사] 토큰 길이가 유효하지 않습니다:', token.length);
        return false;
      }
      
      // FCM 토큰은 콜론(:)을 포함해야 함
      if (!token.includes(':')) {
        console.warn('⚠️ [FCM 토큰 검사] 토큰에 콜론이 없습니다');
        return false;
      }
      
      // FCM 토큰은 알파벳, 숫자, 콜론, 하이픈, 언더스코어만 포함
      const validPattern = /^[A-Za-z0-9:_-]+$/;
      if (!validPattern.test(token)) {
        console.warn('⚠️ [FCM 토큰 검사] 토큰에 유효하지 않은 문자가 포함되어 있습니다');
        return false;
      }
      
      console.log('✅ [FCM 토큰 검사] 토큰 형식이 유효합니다');
      return true;
    } catch (error) {
      console.error('❌ [FCM 토큰 검사] 토큰 검사 중 오류:', error);
      return false;
    }
  }


  // 브라우저 구독 복원 (서버 구독이 있을 때)
  private async restoreBrowserSubscription(): Promise<boolean> {
    try {
      console.log('🔄 [브라우저 구독 복원] 서버 구독을 기반으로 브라우저 구독 복원 시작...');
      
      // 기기별 구독 상태 확인 (우선순위)
      const deviceSubscriptionStatus = this.getDeviceSubscriptionStatus();
      if (!deviceSubscriptionStatus) {
        console.log('🚫 [브라우저 구독 복원] 기기별 구독 비활성화됨 - 브라우저 구독 복원 중단');
        return false;
      }
      console.log('✅ [브라우저 구독 복원] 기기별 구독 활성화됨 - 브라우저 구독 복원 진행');
      
      // Service Worker 등록 확인
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('❌ [브라우저 구독 복원] Push API 미지원');
        return false;
      }
      
      // Service Worker 준비 대기
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ [브라우저 구독 복원] Service Worker 준비 완료');
      
      // VAPID 공개키 가져오기
      const vapidPublicKey = await this.getVapidPublicKey();
      if (!vapidPublicKey) {
        console.error('❌ [브라우저 구독 복원] VAPID 공개키 조회 실패');
        return false;
      }
      
      // 브라우저 구독 생성
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey) as any
      });
      
      if (subscription) {
        console.log('✅ [브라우저 구독 복원] 브라우저 구독 생성 성공');
        
        // 서버에 새로운 구독 정보 전송
        const p256dhKey = subscription.getKey('p256dh');
        const authKey = subscription.getKey('auth');
        
        if (!p256dhKey || !authKey) {
          console.error('❌ [브라우저 구독 복원] 구독 키가 없음');
          return false;
        }
        
        const subscriptionData = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: this.arrayBufferToBase64(p256dhKey),
            auth: this.arrayBufferToBase64(authKey)
          }
        };
        const settings: NotificationSettings = {
          dailyReport: true,
          inboundReminder: true,
          realtimeLowStock: true,
          newMenu: true,
          orderDeadline: true,
          // 하위 호환성
          lowStock: true,
          orderAvailable: true,
          inboundComplete: true,
          system: true
        };
        const success = await this.subscribe(subscriptionData, settings, true);
        if (success) {
          console.log('✅ [브라우저 구독 복원] 서버 구독 업데이트 성공');
          return true;
        } else {
          console.error('❌ [브라우저 구독 복원] 서버 구독 업데이트 실패');
          return false;
        }
      } else {
        console.error('❌ [브라우저 구독 복원] 브라우저 구독 생성 실패');
        return false;
      }
    } catch (error) {
      console.error('❌ [브라우저 구독 복원] 복원 중 오류:', error);
      return false;
    }
  }

  // VAPID 키를 Uint8Array로 변환
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // ArrayBuffer를 Base64로 변환
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // iOS Safari 전용 오류 리포트 전송
  private async sendIOSSafariErrorReport(error: any, operation: string, debugInfo: object): Promise<void> {
    try {
      const userId = this.getUserId();
      const baseUrl = getSafeApiUrl('NOTIFICATION');
      
      const errorReport = {
        userId,
        platform: 'iOS Safari',
        operation,
        error: {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : undefined
        },
        debugInfo,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };
      
      console.log('🍎 [iOS Safari] 오류 리포트 전송:', errorReport);
      
      const response = await fetch(`${baseUrl}/push/ios-safari-error-report`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(errorReport)
      });
      
      if (response.ok) {
        console.log('✅ [iOS Safari] 오류 리포트 전송 성공');
      } else {
        console.warn('⚠️ [iOS Safari] 오류 리포트 전송 실패:', response.status);
      }
    } catch (reportError) {
      console.error('❌ [iOS Safari] 오류 리포트 전송 중 오류:', reportError);
    }
  }

  // UI 상태 동기화를 위한 구독 변경 알림
  private notifySubscriptionChange(isSubscribed: boolean): void {
    try {
      // 커스텀 이벤트를 발생시켜 UI 컴포넌트에 알림
      const event = new CustomEvent('pushSubscriptionChanged', {
        detail: { isSubscribed }
      });
      window.dispatchEvent(event);
      console.log('📢 [UI 동기화] 구독 상태 변경 알림 전송:', isSubscribed);
    } catch (error) {
      console.error('❌ [UI 동기화] 구독 상태 변경 알림 실패:', error);
    }
  }

  public async requestAutoResubscribe(reason: string = 'manual-trigger'): Promise<boolean> {
    return this.triggerAutoResubscribe(reason);
  }

  private getStoredNotificationSettings(): NotificationSettings {
    const defaultSettings: NotificationSettings = {
      dailyReport: true,
      inboundReminder: true,
      realtimeLowStock: true,
      newMenu: true,
      orderDeadline: true
    };

    try {
      const saved = localStorage.getItem('notificationSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          dailyReport: parsed.dailyReport ?? defaultSettings.dailyReport,
          inboundReminder: parsed.inboundReminder ?? defaultSettings.inboundReminder,
          realtimeLowStock: parsed.realtimeLowStock ?? defaultSettings.realtimeLowStock,
          newMenu: parsed.newMenu ?? defaultSettings.newMenu,
          orderDeadline: parsed.orderDeadline ?? defaultSettings.orderDeadline,
          // 하위 호환성
          lowStock: parsed.realtimeLowStock ?? parsed.lowStock ?? true,
          orderAvailable: parsed.orderAvailable ?? true,
          inboundComplete: parsed.inboundComplete ?? true,
          system: parsed.system ?? true
        };
      }
    } catch (error) {
      console.warn('⚠️ [자동 재구독] 저장된 알림 설정 파싱 실패, 기본값 사용:', error);
    }

    return defaultSettings;
  }

  private async triggerAutoResubscribe(reason: string): Promise<boolean> {
    if (this.isAutoRefreshing) {
      console.log('⏳ [자동 재구독] 이미 진행 중 - 중복 실행 방지');
      return false;
    }

    if (!this.isPureMobile()) {
      console.log('🚫 [자동 재구독] 순수 모바일 환경이 아니어서 중단');
      return false;
    }

    const isIOSSafari = this.isIOSSafari();

    if (!isIOSSafari && typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      console.warn('⚠️ [자동 재구독] 브라우저 알림 권한 거부 상태 - 자동 재구독 중단');
      return false;
    }

    if (!isPushNotificationSupported()) {
      console.warn('⚠️ [자동 재구독] 현재 환경에서 푸시 알림 미지원');
      return false;
    }

    this.isAutoRefreshing = true;

    try {
      console.log(`🔄 [자동 재구독] 시작 - 사유: ${reason}`);

      if (!isIOSSafari) {
        try {
          await registerServiceWorker();
        } catch (swError) {
          console.warn('⚠️ [자동 재구독] Service Worker 등록 실패 (무시하고 진행):', swError);
        }
      }

      const vapidKey = getVapidPublicKey();
      // 알림 재활성화 시 강제로 새 구독 생성 (기존 무효 구독 방지)
      const wasDisabled = localStorage.getItem('user_disabled_subscription') === 'true';
      const rawSubscription = await subscribeToPush(vapidKey, wasDisabled);
      const subscriptionData = serializePushSubscription(rawSubscription);
      const currentSettings = this.getStoredNotificationSettings();

      const success = await this.subscribe(subscriptionData, currentSettings, true, true);

      if (success) {
        console.log('✅ [자동 재구독] 성공적으로 완료');
      } else {
        console.warn('⚠️ [자동 재구독] 서버 구독 업데이트 실패');
      }

      return success;
    } catch (error) {
      console.error('❌ [자동 재구독] 실행 중 오류:', error);
      return false;
    } finally {
      this.isAutoRefreshing = false;
    }
  }
}

// 싱글톤 인스턴스 생성
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
export type { NotificationSettings }; 